import React, { forwardRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { FaSpinner } from "react-icons/fa";
import { StarRate } from "@mui/icons-material";
import AOS from "aos";
import "aos/dist/aos.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import LoginModal from "../../../../modal/LoginModal";
import RegisterModal from "../../../../modal/RegisterModal";
import ForgotPasswordModal from "../../../../modal/ForgotPasswordModal";
import VerifyOtpModal from "../../../../modal/VerifyOtpModal";
import "./productCard.css";
import { t } from "i18next";
import { useModal } from "../../../../hooks/ModalContext";

// ----------------- Rating Component -----------------
export const Rating = ({ averageRating, totalReviews }) => {
  const fillPercentage = (averageRating / 5) * 100;

  return (
    <span className="d-flex align-items-center">
      <span style={{ position: "relative", display: "inline-block", lineHeight: "0" }}>
        <StarRate style={{ color: "var(--light-gray)", fontSize: "18px" }} />
        <span
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${fillPercentage}%`,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <StarRate style={{ color: "orange", fontSize: "18px" }} />
        </span>
      </span>

      <span
        className="text-dark ms-1"
        style={{ fontSize: "14px", fontWeight: 500 }}
      >
        {averageRating?.toFixed(1) || 0} ({totalReviews})
      </span>
    </span>
  );
};

// ----------------- Product Component -----------------
const Product = forwardRef(
  ({ product, showFilters, onAddToCart, onAddToWishlist, onRemoveWishlist }, ref) => {
    const { title, sales_price, id, image, in_cart, in_whishlist } = product;
    // Safely get first image or fallback to placeholder
    const imageUrl = image || "https://royshop.in/assets/no-image.png";

    //redux state
    const { api_token } = useSelector((state) => state.auth);
    const [isFavorited, setIsFavorited] = useState(in_whishlist || false);
    const [isAddedToCart, setIsAddedToCart] = useState(in_cart || false);
    const [isLoading, setIsLoading] = useState(false);

    const { activeModal, setActiveModal, emailForOtp, setEmailForOtp } = useModal();

    const { emailForOtp: emailForOtpLogin } = useSelector((state) => state.auth);
    const { emailForOtp: emailForOtpRegister } = useSelector(
      (state) => state.register
    );

    useEffect(() => {
      AOS.init({ duration: 1000 });
    }, []);

    const toggleFavorite = () => {
      if (!api_token) {
        toast.error(`${t("You_need_to_login_to_add_product_to_your_wishlist")}`, {
          hideProgressBar: true,
          position: 'bottom-right',
          theme: 'dark',

        });
        setActiveModal("login");
        return;
      }

      setIsFavorited(prev => {
        if (prev) onRemoveWishlist?.(product);
        else onAddToWishlist?.(product);
        return !prev;
      });
    };

    const handleAddToCart = () => {
      if (!api_token) {
        toast.error(`${t("You_need_to_login_to_add_product_to_your_cart")}`, {
          hideProgressBar: true,
          position: 'bottom-right',
          theme: 'dark',
        });
        setActiveModal("login");
        return;
      }

      setIsLoading(true);
      onAddToCart?.(product);
      setTimeout(() => {
        setIsAddedToCart(true);
        setIsLoading(false);
      }, 400);
    };

    const handleBuyNow = () => alert(`Buy Now clicked for ${title}`);

    return (
      <>
        <div ref={ref} className={`col-6 ${showFilters ? "col-xl-3" : "col-xl-2"}`}>
          <div
            className="card rounded-3 shadow-sm product-card position-relative bg-white h-100"
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FontAwesomeIcon
              icon={isFavorited ? solidHeart : regularHeart}
              className="favorite-icon"
              onClick={toggleFavorite}
            />

            <Link to={`/product/${id}`}>
              <div className="product-image-container position-relative d-flex justify-content-center align-items-center p-3" style={{ height: "220px" }}>
                <img
                  className="card-img-top product-image"
                  alt='No Image'
                  onError={(e) => {
                    e.target.onerror = null; // prevents infinite loop
                    e.target.src = "https://amzio.de/api/uploads/products/no-thumbnail.png";
                  }}
                  src={imageUrl}
                  style={{
                    maxHeight: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </Link>

            <div
              className="card-body d-flex flex-column justify-content-between"
              style={{ flexGrow: 1 }}
            >
              <div className="text-start w-100">
                {/* ✅ Allow max 3 lines for title */}
                <Link to={`/product/${id}`}>
                  <h6
                    className="card-title text-start text-dark fs-6 fw-bold d-block three-line-truncate"
                  >
                    {title}
                  </h6>
                </Link>
                <div
                  className="
    d-flex flex-column flex-md-row
    align-items-start align-items-md-center
    mb-3 mt-3 gap-0 gap-md-2
  "
                >
                  <span className="d-flex align-items-center card-text fs-6 fw-bold px-1 m-0">
                    €{sales_price || 0}
                  </span>

                  <Rating averageRating={3} totalReviews={120} />
                </div>

              </div>

              {/* ✅ Buttons pinned to bottom */}
              <div className="d-flex justify-content-center flex-column flex-sm-row pt-3 w-100">
                <button
                  className="btn-stylish me-2 px-3 d-flex align-items-center justify-content-center mb-2 mb-sm-0 w-100"
                  onClick={handleAddToCart}
                  disabled={isLoading || isAddedToCart}
                  style={isAddedToCart ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                >
                  {isLoading ? (
                    <FaSpinner className="spin" />
                  ) : isAddedToCart ? (
                    `${t("added_to_cart")}`
                  ) : (
                    `${t("add_to_cart")}`
                  )}
                </button>
              </div>
            </div>

          </div>

        </div>
      </>
    );
  }
);

export default Product;
